import React, {Suspense} from 'react';
import {ROUTE_PATHS} from '@di-shared/routes';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {useNavigate} from 'react-router';
import MileageSafeDriving, {
  MileageSafeDrivingError,
  MileageSafeDrivingLoading,
} from '@di-shared/features/mileage/safedriving/MileageSafeDriving';
import {ErrorBoundary} from 'react-error-boundary';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import ImageSelector from '@di-shared/features/common/ImageSelector';

interface MileageSafeDrivingPageProps {
  vin: string;
}

function MileageSafeDrivingPage({vin}: MileageSafeDrivingPageProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T40049')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<ConsentBannerLoadingKr />}>
            <ConsentBannerKr vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <div
          className="tabbar_txt tabbar_sticky"
          role="tablist"
        >
          <div className="inner">
            <button
              type="button"
              className="tabbar_item body_large_r"
              onClick={() => navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin), {replace: true})}
            >
              <span className="txt">{t('T40053')}</span>
            </button>
            <button
              type="button"
              className="tabbar_item body_large_r is-selected"
              onClick={() => navigate(ROUTE_PATHS.MILEAGE_SAFE_DRIVING.getFullPath(vin), {replace: true})}
            >
              <span className="txt">{t('T30253')}</span>
            </button>
            <button
              type="button"
              className="tabbar_item body_large_r"
              onClick={() => navigate(ROUTE_PATHS.MILEAGE_BALANCE.getFullPath(vin), {replace: true})}
            >
              <span className="txt">{t('T30177')}</span>
            </button>
          </div>
        </div>
        <div className="tab_content driving_mileage">
          <section className="section section_driving_mileage">
            <h2 className="blind">{t('T30253')}</h2>
            <section className="section">
              <ErrorBoundary fallback={<MileageSafeDrivingError vin={vin} />}>
                <Suspense fallback={<MileageSafeDrivingLoading />}>
                  <MileageSafeDriving vin={vin} />
                </Suspense>
              </ErrorBoundary>
              <div className="driving_banner type_square">
                <LinkTo
                  to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
                  className="driving_banner_box"
                >
                  <span className="driving_banner_txtbox type_reverse">
                    <strong className="driving_banner_title body_large_b">{t('T30256')}</strong>
                    <span className="driving_banner_desc body_small_r">{t('T30257')}</span>
                  </span>
                  <span className="driving_banner_imgbox">
                    <ImageSelector
                      className="driving_banner_img"
                      alt="driving_banner_img"
                      kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_safe_score.png')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_safe_score.png')}
                      genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_safe_score.png')}
                    />
                  </span>
                </LinkTo>
              </div>
            </section>
          </section>
        </div>
      </main>
    </div>
  );
}

export default MileageSafeDrivingPage;
